import React, { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Star, Check, X } from 'lucide-react';
import { useFeedback } from '@dev-streetbeat/chat/react';

import styles from './responseFeedback.module.scss';
import { createLogger } from 'shared/logger';

const log = createLogger('ResponseFeedback');

interface FeedbackButtonsProps {
  messageId: string;
}

type FeedbackModalProps = {
  rating: number;
  onSubmit: (explanation: string) => void;
  onCancel: () => void;
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({ rating, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [explanation, setExplanation] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = () => {
    if (!explanation.trim() && rating <= 3) {
      setError(true);
      return;
    }
    onSubmit(explanation);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>{t('assistant.feedback.improve')}</h3>
          <button className={styles.closeButton} onClick={onCancel}>
            <X size={18} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <p>{t('assistant.feedback.explain')}</p>
          <textarea
            className={`${styles.modalTextarea} ${error ? styles.error : ''}`}
            value={explanation}
            onChange={(e) => {
              setExplanation(e.target.value);
              if (e.target.value.trim()) {
                setError(false);
              }
            }}
            placeholder={t('assistant.feedback.placeholder')}
            rows={4}
          />
          {error && <div className={styles.errorMessage}>{t('assistant.feedback.required')}</div>}
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onCancel}>
            {t('actions.cancel')}
          </button>
          <button className={styles.submitButton} onClick={handleSubmit}>
            {t('actions.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

const StarRating: React.FC<{
  rating: number;
  onRate: (rating: number) => void;
}> = ({ rating, onRate }) => {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  // Display the hovered rating or current rating
  const displayRating = hoveredRating !== null ? hoveredRating : rating;

  return (
    <div className={styles.starRating}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={styles.starButton}
          onClick={() => onRate(star)}
          onMouseEnter={() => setHoveredRating(star)}
          onMouseLeave={() => setHoveredRating(null)}
          aria-label={`Rate ${star} of 5 stars`}
        >
          <Star
            size={24}
            className={`${styles.star} ${displayRating >= star ? styles.filled : ''}`}
            fill={displayRating >= star ? 'currentColor' : 'none'}
          />
        </button>
      ))}
    </div>
  );
};

const FeedbackButtons: React.FC<FeedbackButtonsProps> = memo(({ messageId }) => {
  const { t } = useTranslation();
  const [feedbackStatus, setFeedbackStatus] = useState<'idle' | 'submitted' | 'error'>('idle');
  const [rating, setRating] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);

  const { submitFeedback } = useFeedback();

  // Star rating to satisfaction mapping (1 star = 0, 2 stars = 25, 3 stars = 50, 4 stars = 75, 5 stars = 100)
  const mapRatingToSatisfaction = (stars: number): number => {
    return (stars - 1) * 25;
  };

  const submitFeedbackHandler = async (stars: number, explanation?: string) => {
    try {
      await submitFeedback({
        message_id: messageId,
        satisfaction: mapRatingToSatisfaction(stars),
        explanation,
      });
      setFeedbackStatus('submitted');
    } catch (e) {
      log.error('submit feedback failed', { error: e });
      setFeedbackStatus('error');
    }
  };

  const handleRate = (stars: number) => {
    setRating(stars);

    // For 5 stars, submit directly; otherwise show modal for explanation
    if (stars === 5) {
      submitFeedbackHandler(stars);
    } else {
      setShowModal(true);
    }
  };

  const handleModalSubmit = (explanation: string) => {
    setShowModal(false);
    submitFeedbackHandler(rating, explanation);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    // Reset rating if they cancel the modal
    setRating(0);
  };

  if (feedbackStatus === 'submitted') {
    return (
      <div className={styles.feedbackContainer}>
        <div className={styles.feedbackSubmitted}>
          <Check size={16} />
          <span>{t('assistant.feedback.submitted')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.feedbackContainer}>
      {feedbackStatus === 'error' && <div className={styles.feedbackError}>{t('assistant.feedback.error')}</div>}
      <div className={styles.feedbackText}>{t('assistant.feedback.question')}</div>
      <StarRating rating={rating} onRate={handleRate} />

      {showModal && <FeedbackModal rating={rating} onSubmit={handleModalSubmit} onCancel={handleModalCancel} />}
    </div>
  );
});

FeedbackButtons.displayName = 'FeedbackButtons';

export default FeedbackButtons;
